import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, Plus, Edit, Trash2, Shield, ShieldCheck, X, 
  Loader2, Search, UserPlus, Eye, EyeOff, FolderKanban
} from 'lucide-react';
import { toast } from 'sonner';

const UserManagement = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Check if current user is a Director (has can_approve_transfers)
  const isDirector = user?.can_approve_transfers === true;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'org_admin',
    campaign_access: [],
    can_approve_transfers: false
  });

  useEffect(() => {
    // Allow access if user is Super Admin OR Director
    if (!isSuperAdmin() && !isDirector) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isSuperAdmin, isDirector, navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, campaignsRes] = await Promise.all([
        api.get('/users'),
        api.get('/campaigns')
      ]);
      setUsers(usersRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'org_admin',
      campaign_access: [],
      can_approve_transfers: false
    });
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email,
      password: '',
      role: user.role,
      campaign_access: user.campaign_access || [],
      can_approve_transfers: user.can_approve_transfers || false
    });
    setShowPassword(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate: Org admins must have at least one campaign
    if (formData.role === 'org_admin' && formData.campaign_access.length === 0) {
      toast.error('Organisation admins must have at least one event assigned');
      return;
    }
    
    // Validate: Super Admins cannot create Directors
    if (!isDirector && formData.can_approve_transfers) {
      toast.error('Only Directors can create other Directors');
      return;
    }
    
    setSaving(true);

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          role: formData.role,
          campaign_access: formData.campaign_access,
          can_approve_transfers: formData.can_approve_transfers
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully!');
      } else {
        // Create new user
        await api.post('/auth/register', {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          campaign_access: formData.campaign_access,
          can_approve_transfers: formData.can_approve_transfers
        });
        toast.success('User created successfully!');
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    setDeleting(userId);
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const toggleCampaignAccess = (campaignId) => {
    setFormData(prev => ({
      ...prev,
      campaign_access: prev.campaign_access.includes(campaignId)
        ? prev.campaign_access.filter(id => id !== campaignId)
        : [...prev.campaign_access, campaignId]
    }));
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || 'Unknown Event';
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900">User Management</h1>
              <p className="text-sm font-jakarta text-slate-600 mt-1">
                Manage organisation admins and their event access
              </p>
            </div>
            <Button
              onClick={openAddModal}
              className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search */}
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10"
            />
          </div>
        </Card>

        {/* Users List */}
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-lg font-outfit font-semibold text-slate-900">
              All Users ({filteredUsers.length})
            </h2>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-jakarta">No users found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredUsers.map((listUser) => (
                <div key={listUser.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        listUser.can_approve_transfers 
                          ? 'bg-amber-100' 
                          : listUser.role === 'super_admin' 
                            ? 'bg-primary/10' 
                            : 'bg-secondary/10'
                      }`}>
                        {listUser.can_approve_transfers ? (
                          <ShieldCheck className="w-5 h-5 text-amber-600" />
                        ) : listUser.role === 'super_admin' ? (
                          <ShieldCheck className="w-5 h-5 text-primary" />
                        ) : (
                          <Shield className="w-5 h-5 text-secondary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-jakarta font-medium text-slate-900">
                            {listUser.name || 'Unnamed User'}
                          </p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            listUser.can_approve_transfers
                              ? 'bg-amber-100 text-amber-700'
                              : listUser.role === 'super_admin'
                                ? 'bg-primary/10 text-primary'
                                : 'bg-secondary/10 text-secondary'
                          }`}>
                            {listUser.can_approve_transfers 
                              ? 'Director' 
                              : listUser.role === 'super_admin' 
                                ? 'Super Admin' 
                                : 'Org Admin'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mb-2">{listUser.email}</p>
                        
                        {/* Campaign Access Tags */}
                        {listUser.role === 'org_admin' && !listUser.can_approve_transfers && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <FolderKanban className="w-3.5 h-3.5 text-slate-400" />
                            {listUser.campaign_access && listUser.campaign_access.length > 0 ? (
                              listUser.campaign_access.map(campaignId => (
                                <span 
                                  key={campaignId}
                                  className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600"
                                >
                                  {getCampaignName(campaignId)}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400 italic">No events assigned</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(listUser)}
                        className="hover:bg-slate-100 h-8 w-8 p-0"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {listUser.role !== 'super_admin' && !listUser.can_approve_transfers && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(listUser.id)}
                          disabled={deleting === listUser.id}
                          className="hover:bg-red-50 text-red-500 h-8 w-8 p-0"
                          title="Delete User"
                        >
                          {deleting === listUser.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-outfit font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter user name"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter email address"
                    required
                    disabled={!!editingUser}
                    className={editingUser ? 'bg-slate-50 text-slate-500' : ''}
                  />
                  {editingUser && (
                    <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder={editingUser ? 'Enter new password (optional)' : 'Enter password'}
                      required={!editingUser}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Role</Label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value, can_approve_transfers: false})}
                    className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="org_admin">Organisation Admin (Read-Only)</option>
                    <option value="super_admin">Super Admin (Full Access)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.role === 'org_admin'
                      ? 'Can view assigned events and registrations but cannot edit'
                      : 'Full access to create, edit, and delete all data'
                    }
                  </p>
                </div>
                
                {/* Director Access - Only visible to Directors */}
                {isDirector && formData.role === 'super_admin' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.can_approve_transfers}
                        onChange={(e) => setFormData({...formData, can_approve_transfers: e.target.checked})}
                        className="h-4 w-4 mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Grant Director Access</p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          Directors can approve fund transfers, view Kaizen&apos;s share, and manage all user roles including other Directors.
                        </p>
                      </div>
                    </label>
                  </div>
                )}
                
                {formData.role === 'org_admin' && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Event Access
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Select which events this user can view
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                      {campaigns.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-2">No events available</p>
                      ) : (
                        campaigns.map(campaign => (
                          <label
                            key={campaign.id}
                            className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.campaign_access.includes(campaign.id)}
                              onChange={() => toggleCampaignAccess(campaign.id)}
                              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                            />
                            <div className="flex-1">
                              <span className="text-sm text-slate-700">{campaign.name}</span>
                              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                              }`}>
                                {campaign.status}
                              </span>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {formData.campaign_access.length} event(s) selected
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary hover:bg-primary-hover"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingUser ? 'Update User' : 'Create User'
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
